import React from "react";
import { Grid, TextField } from "@mui/material";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { GlobalBox } from "components/Box/GlobalBox";
import { useRevalidateFetch } from "hooks/useRevalidateFetch";
import { getSatUsages } from "redux/actions/creditNotes/creditNotesActions";
import { getPayments } from "redux/actions/deposits/depositAction";
import { useSelector } from "react-redux";
import { useForm } from "hooks/useForm";
import { initialValues, requiredValues } from "./inputValues";
import RegularButton from "components/CustomButtons/Button";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import { blue, red } from "@mui/material/colors";
import { paymentsInvoicesType } from "helpers/dictionary";
import { useReceiverHook } from "./useReceiverHook";
import { regimensData } from "enums/regimens";
import { EmailForm } from "views/Invoices/EmailForm";
import { reasonNotesOptions, typePayment, receiverConfig } from "./config"

export const ReceiverInvoice = () => {
  const { satUsages } = useSelector(({ creditNotes }) => creditNotes);
  const { payments } = useSelector(({ deposits }) => deposits);
  const { reasonSelect } = useSelector(({ cashInvoices }) => cashInvoices);
  
  const { values, onChange, setValues, validate, errors, reset, setFieldErrors } = useForm(
    initialValues.receiver,
    requiredValues.receiverRequired
  );

  const {
    idAccount,
    companyName,
    rfc,
    email,
    emails,
    regime,
    satUsage,
    cp,
    paymentMethod,
    paymentType,
    searchCustomer,
    editData,
  } = values;

  const {
    submit,
    loading,
    mapPayments,
    handleChangePaymentMethod,
    handlePaymentType,
    handleRfc,
    handleEditData
  } = useReceiverHook({
    values,
    setValues,
    setFieldErrors,
    validate,
    reset,
  });
  useRevalidateFetch(getSatUsages, satUsages);
  useRevalidateFetch(getPayments, payments);

  return (
    <>
      <Grid item xs={12} md={6}>
        <GlobalBox
          title={receiverConfig.title}
          head
          style={{ boxShadow: "none", margin: 0 }}
          styleBody={{ paddingLeft: 0, paddingRight: 0 }}
        >
          <TextField
            variant="outlined"
            label={receiverConfig.fields.searchCustomer.label}
            name={receiverConfig.fields.searchCustomer.name}
            value={searchCustomer}
            onChange={handleRfc}
            fullWidth
            disabled={loading}
            error={errors.searchCustomer}
            helperText={errors.searchCustomer}
            InputProps={{
              endAdornment: (
                <>
                  <RegularButton
                    justIcon
                    round
                    color="transparent"
                    size="sm"
                    tooltip={receiverConfig.fields.searchCustomer.tooltip}
                    onClick={submit}
                    loading={loading}
                  >
                    <SearchIcon sx={{ color: blue[700] }} />
                  </RegularButton>

                  <RegularButton
                    justIcon
                    round
                    color="transparent"
                    size="sm"
                    tooltip={receiverConfig.fields.searchCustomer.cleanTooltip}
                    onClick={(e) => {
                      e.preventDefault();
                      setValues({ searchCustomer: "", idAccount: null });
                    }}
                    loading={loading}
                  >
                    <ClearIcon />
                  </RegularButton>
                </>
              ),
            }}
          />
          <TextField
            variant="outlined"
            label={reasonSelect === reasonNotesOptions.foreign ?
              receiverConfig.fields.companyName.labelForeign :
              receiverConfig.fields.companyName.label
            }
            name={receiverConfig.fields.companyName.name}
            value={companyName}
            onChange={onChange}
            fullWidth
            error={errors.companyName}
            helperText={errors.companyName}
            disabled={!editData || !!idAccount?.name}
          />
          <TextField
            variant="outlined"
            label={receiverConfig.fields.rfc.label}
            name={receiverConfig.fields.rfc.name}
            value={rfc}
            onChange={handleRfc}
            fullWidth
            error={errors.rfc}
            helperText={errors.rfc}
            disabled={!editData || !!idAccount?.rfc || reasonSelect === reasonNotesOptions.foreign}
            InputProps={{
              endAdornment: (
                <>
                  <RegularButton
                    justIcon
                    round
                    color="transparent"
                    size="sm"
                    tooltip={editData ? receiverConfig.fields.rfc.tooltipCancel : receiverConfig.fields.rfc.tooltip}
                    onClick={handleEditData}
                    loading={loading}
                  >
                    <EditIcon sx={{ color: editData ? red[500] : blue[700] }} />
                  </RegularButton>
                </>
              ),
            }}
          />
          <EmailForm
            email={email}
            emails={emails}
            onChange={onChange}
            setValues={setValues}
            reset={reset}
            errors={errors}
          />
          <AutocompleteComponent
            label={receiverConfig.fields.regime.label}
            options={regimensData}
            name={receiverConfig.fields.regime.name}
            value={regime}
            handleValues={setValues}
            disabled={!editData || reasonSelect === reasonNotesOptions.foreign}
            textFieldProps={{
              error: errors.regime,
              helperText: errors.regime,
            }}
          />
          <Grid container spacing={1} style={{ paddingRight: 0 }}>
            <Grid item xs={12} md={8}>
              <AutocompleteComponent
                label={receiverConfig.fields.paymentType.label}
                options={mapPayments}
                name={receiverConfig.fields.paymentType.name}
                value={paymentType}
                handleValues={handlePaymentType}
                textFieldProps={{
                  error: errors.paymentType,
                  helperText: errors.paymentType,
                }}
                disabled={paymentMethod?.value === typePayment.ppd}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <AutocompleteComponent
                label={receiverConfig.fields.paymentMethod.label}
                options={paymentsInvoicesType}
                name={receiverConfig.fields.paymentMethod.name}
                value={paymentMethod}
                handleValues={handleChangePaymentMethod}
                textFieldProps={{
                  error: errors.paymentMethod,
                  helperText: errors.paymentMethod,
                }}
                disabled={reasonSelect === reasonNotesOptions.foreign}
              />
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12} md={8} sx={{ paddingBottom: 0, }} >
              <AutocompleteComponent
                label={receiverConfig.fields.satUsage.label}
                options={satUsages}
                name={receiverConfig.fields.satUsage.name}
                value={satUsage}
                handleValues={setValues}
                textFieldProps={{
                  error: errors.satUsage,
                  helperText: errors.satUsage,
                }}
                disabled={!editData || reasonSelect === reasonNotesOptions.foreign}
              />
            </Grid>
            <Grid item xs={12} md={4} sx={{ paddingRight: 0, }} >
              <TextField
                variant="outlined"
                label={receiverConfig.fields.cp.label}
                name={receiverConfig.fields.cp.name}
                value={cp}
                onChange={onChange}
                fullWidth
                inputProps={{ right: "1" }}
                error={errors.cp}
                helperText={errors.cp}
                disabled={!editData || reasonSelect === reasonNotesOptions.foreign}
              />
            </Grid>
          </Grid>
        </GlobalBox>
      </Grid>
    </>
  );
};
